import { httpClient } from '../api/http-client';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/api';

export interface MensajeChat {
  id: string;
  pedidoId: string;
  remitente: 'CLIENTE' | 'ADMIN' | 'SISTEMA';
  nombreRemitente: string;
  emailRemitente?: string;
  mensaje: string;
  tipo: string;
  metadata?: any;
  leido: boolean;
  leidoEn?: string;
  creadoEn: string;
}

export interface EnviarMensajeRequest {
  mensaje: string;
  tipo?: string;
  nombreRemitente?: string;
  emailRemitente?: string;
}

export class ChatService {
  private static readonly BASE_PATH = '/pedidos';
  private static readonly PUBLIC_PATH = '/pedidos-publico';

  /**
   * Obtener mensajes de un pedido (admin)
   */
  static async getMensajes(pedidoId: string): Promise<{ success: boolean; data: MensajeChat[]; message?: string }> {
    try {
      const response = await httpClient.get<{ success: boolean; data: MensajeChat[] }>(
        `${this.BASE_PATH}/${pedidoId}/mensajes`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Error al cargar mensajes',
      };
    }
  }

  /**
   * Enviar mensaje como admin
   */
  static async enviarMensaje(
    pedidoId: string,
    data: EnviarMensajeRequest
  ): Promise<{ success: boolean; data?: MensajeChat; message?: string }> {
    try {
      const response = await httpClient.post<{ success: boolean; data: MensajeChat }>(
        `${this.BASE_PATH}/${pedidoId}/mensajes`,
        data
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al enviar mensaje',
      };
    }
  }

  /**
   * Marcar mensajes como leídos (admin)
   */
  static async marcarLeidos(pedidoId: string): Promise<{ success: boolean; count?: number; message?: string }> {
    try {
      const response = await httpClient.patch<{ success: boolean; data: { count: number } }>(
        `${this.BASE_PATH}/${pedidoId}/mensajes/leidos`
      );
      return {
        success: response.data.success,
        count: response.data.data?.count,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al marcar como leídos',
      };
    }
  }

  // ============ MÉTODOS PÚBLICOS PARA CLIENTES ============

  /**
   * Obtener mensajes de un pedido (cliente - sin auth)
   */
  static async getMensajesCliente(pedidoId: string): Promise<{ success: boolean; data: MensajeChat[]; message?: string }> {
    try {
      // Usar axios directamente para evitar el interceptor de auth
      const response = await axios.get<{ success: boolean; data: MensajeChat[] }>(
        `${API_BASE_URL}${this.PUBLIC_PATH}/${pedidoId}/mensajes`
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Error al cargar mensajes',
      };
    }
  }

  /**
   * Enviar mensaje como cliente
   */
  static async enviarMensajeCliente(
    pedidoId: string,
    data: EnviarMensajeRequest
  ): Promise<{ success: boolean; data?: MensajeChat; message?: string }> {
    try {
      // Usar axios directamente para evitar el interceptor de auth
      const response = await axios.post<{ success: boolean; data: MensajeChat }>(
        `${API_BASE_URL}${this.PUBLIC_PATH}/${pedidoId}/mensajes`,
        data
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al enviar mensaje',
      };
    }
  }

  /**
   * Marcar mensajes como leídos (cliente)
   */
  static async marcarLeidosCliente(pedidoId: string): Promise<{ success: boolean; count?: number; message?: string }> {
    try {
      // Usar axios directamente para evitar el interceptor de auth
      const response = await axios.patch<{ success: boolean; data: { count: number } }>(
        `${API_BASE_URL}${this.PUBLIC_PATH}/${pedidoId}/mensajes/leidos`
      );
      return {
        success: response.data.success,
        count: response.data.data.count,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al marcar como leídos',
      };
    }
  }

  /**
   * Obtener conteo de mensajes no leídos para múltiples pedidos (admin)
   */
  static async obtenerNoLeidosBatch(pedidoIds: string[]): Promise<{ success: boolean; data?: Record<string, number>; message?: string }> {
    try {
      const response = await httpClient.post<{ success: boolean; data: Record<string, number> }>(
        '/chat-admin/no-leidos',
        { pedidoIds }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener mensajes no leídos',
      };
    }
  }

  /**
   * Obtener conteo total de mensajes no leídos (admin)
   */
  static async obtenerTotalNoLeidos(): Promise<{ success: boolean; count?: number; message?: string }> {
    try {
      const response = await httpClient.get<{ success: boolean; data: { count: number } }>(
        '/chat-admin/no-leidos/total'
      );
      return {
        success: response.data.success,
        count: response.data.data?.count,
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener total de mensajes no leídos',
      };
    }
  }
}
